import { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"

const eras = [
  {
    id: "classic",
    name: "Classic Mac",
    year: "1984 – 2000",
    philosophy: "Function first. Structure over decoration.",
    description: "Utilitarian, pixel-precise, system-native. No blur, no glass, no soft shadow — just direct, readable hierarchy.",
    command: "/apply classic to modal",
    tags: ["utility", "precision", "pixel"],
  },
  {
    id: "aqua",
    name: "Aqua",
    year: "2000 – 2007",
    philosophy: "Emotional, tactile UI. Digital made touchable.",
    description: "Glossy gradients, jelly-like surfaces, inner shadows, and reflective highlights create a playful yet premium feel.",
    command: "/apply aqua to button",
    tags: ["glossy", "playful", "premium"],
  },
  {
    id: "brushed",
    name: "Brushed Metal",
    year: "2002 – 2008",
    philosophy: "Digital meets hardware. Software as an object.",
    description: "Silver graphite surfaces, horizontal grain texture, dark chrome toolbars — interface as physical instrument.",
    command: "/apply brushed to nav",
    tags: ["metallic", "industrial", "craft"],
  },
  {
    id: "skeuomorphic",
    name: "Skeuomorphic",
    year: "2007 – 2013",
    philosophy: "Familiarity through realism. Texture as language.",
    description: "Leather, paper, wood and stitched details make software feel like a known physical object.",
    command: "/apply skeuomorphic to card",
    tags: ["leather", "texture", "tactile"],
  },
  {
    id: "flat",
    name: "Flat / iOS 7",
    year: "2013 – 2016",
    philosophy: "Chrome is the enemy. Colour is the material.",
    description: "Vibrant mesh gradients, ultralight Helvetica Neue, translucent layers. Decoration stripped; pure hue and typography carry all meaning.",
    command: "/apply flat to dashboard",
    tags: ["gradient", "ultralight", "translucent"],
  },
  {
    id: "blur",
    name: "Blur / iOS 13+",
    year: "2016 – 2023",
    philosophy: "Layered hierarchy. Translucency as depth.",
    description: "Backdrop blur and vibrancy communicate layer structure. Soft borders, ambient shadows, smooth transitions.",
    command: "/apply blur to panel",
    tags: ["translucent", "layered", "modern"],
  },
  {
    id: "liquid",
    name: "Liquid Glass",
    year: "2024 →",
    philosophy: "Material becomes alive. Interface as responsive matter.",
    description: "High blur, light refraction, floating layers, dynamic highlights and soft morphing edges.",
    command: "/apply liquid to hero",
    tags: ["glass", "refraction", "floating"],
  },
]

function ClassicPreview() {
  return (
    <div className="skill-mock skill-mock-classic">
      <div className="sk-classic-titlebar">
        <span className="sk-classic-dot" />
        <span className="sk-classic-title">Untitled</span>
      </div>
      <div className="sk-classic-menu">File  Edit  View  Window</div>
      <div className="sk-classic-body">
        <div className="sk-classic-row" />
        <div className="sk-classic-row sk-classic-row--short" />
        <div className="sk-classic-row" />
        <div className="sk-classic-btn">OK</div>
      </div>
    </div>
  )
}

function AquaPreview() {
  return (
    <div className="skill-mock skill-mock-aqua">
      <div className="sk-aqua-window">
        <div className="sk-aqua-dots">
          <span style={{ background: "#ff6057" }} />
          <span style={{ background: "#ffbd2e" }} />
          <span style={{ background: "#27c93f" }} />
        </div>
        <div className="sk-aqua-content">
          <div className="sk-aqua-text-row" />
          <div className="sk-aqua-text-row sk-aqua-text-row--short" />
          <div className="sk-aqua-buttons">
            <button className="sk-aqua-btn sk-aqua-btn--blue">Confirm</button>
            <button className="sk-aqua-btn sk-aqua-btn--gray">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  )
}

function BrushedPreview() {
  return (
    <>
    <img src="/macos2000.png" alt="" className="sk-brushed-desktop-bg" />
    <div className="skill-mock skill-mock-brushed">
      {/* Brushed metal title bar */}
      <div className="sk-brushed-titlebar">
        <div className="sk-brushed-left-controls">
          <div className="sk-brushed-wc">
            <span className="sk-brushed-dot" style={{ background: "#ff6057" }} />
            <span className="sk-brushed-dot" style={{ background: "#ffbd2e" }} />
            <span className="sk-brushed-dot" style={{ background: "#27c93f" }} />
          </div>
          <div className="sk-brushed-nav">
            <span className="sk-brushed-navbtn">‹</span>
            <span className="sk-brushed-navbtn">›</span>
          </div>
          <span className="sk-brushed-showall">Show All</span>
        </div>
        <span className="sk-brushed-wintitle">iCloud</span>
        <div className="sk-brushed-searchbox"><span>⌕</span></div>
      </div>

      {/* White content area */}
      <div className="sk-brushed-content">
        <div className="sk-brushed-logo">iCloud</div>
        <div className="sk-brushed-tagline">
          iCloud stores your content and wirelessly pushes it to your devices.
        </div>

        {/* Icon fan — cloud is the hero, elevated in center */}
        <div className="sk-brushed-icons">
          <div className="sk-brushed-icon sk-brushed-icon--itunes">♪</div>
          <div className="sk-brushed-icon sk-brushed-icon--notes">✎</div>
          <div className="sk-brushed-icon sk-brushed-icon--photo">🌻</div>
          <div className="sk-brushed-icon sk-brushed-icon--cloud">
            <svg viewBox="0 0 48 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M38 33H12C8.1 33 5 29.9 5 26c0-3.2 2.1-5.9 5.1-6.8-.1-.4-.1-.8-.1-1.2C10 13.6 13.6 10 18 10c1.6 0 3.1.5 4.4 1.3C24 8 27.6 5.5 31.8 5.5 37.4 5.5 42 10.1 42 15.7c0 .5 0 1-.1 1.5C44.3 18.1 46 20.9 46 24c0 5-4 9-9 9h1z" fill="url(#cg2)" />
              <defs>
                <linearGradient id="cg2" x1="5" y1="5" x2="46" y2="33" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#e8e8e8"/>
                  <stop offset="45%" stopColor="#a8a8a8"/>
                  <stop offset="100%" stopColor="#d0d0d0"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
          <div className="sk-brushed-icon sk-brushed-icon--mail">✉</div>
          <div className="sk-brushed-icon sk-brushed-icon--cal">
            <div className="sk-brushed-cal-top">Monday</div>
            <div className="sk-brushed-cal-num">6</div>
          </div>
          <div className="sk-brushed-icon sk-brushed-icon--contacts">👤</div>
        </div>
      </div>

      {/* Sign in strip */}
      <div className="sk-brushed-signin">
        <div className="sk-brushed-signin-label">Use iCloud with your Apple ID:</div>
        <div className="sk-brushed-signin-row">
          <div className="sk-brushed-field sk-brushed-field--active">Apple ID</div>
          <div className="sk-brushed-field">Password</div>
          <button className="sk-brushed-signin-btn">Sign In</button>
        </div>
        <div className="sk-brushed-signin-links">
          <span>Create an Apple ID…</span>
          <span>Forgot password?</span>
        </div>
      </div>
    </div>
    </>
  )
}

function SkeuomorphicPreview() {
  return (
    <div className="skill-mock skill-mock-skeuomorphic">
      <div className="sk-skeuo-header">
        <span className="sk-skeuo-title">Notes</span>
        <span className="sk-skeuo-date">Today</span>
      </div>
      <div className="sk-skeuo-lines">
        {[1,2,3,4,5].map(i => (
          <div key={i} className="sk-skeuo-line">
            <div className="sk-skeuo-text" style={{ width: `${[85,70,90,60,75][i-1]}%` }} />
          </div>
        ))}
      </div>
    </div>
  )
}

function FlatPreview() {
  return (
    <div className="skill-mock skill-mock-flat">
      {/* Status bar */}
      <div className="sk-flat-status">
        <span className="sk-flat-time-sm">9:41</span>
        <div className="sk-flat-status-right">
          <svg width="12" height="10" viewBox="0 0 12 10" fill="white" opacity="0.9"><path d="M6 1L11 9H1L6 1z"/></svg>
          <svg width="22" height="10" viewBox="0 0 22 10" fill="none"><rect x="0" y="1" width="18" height="8" rx="2" stroke="white" strokeWidth="1.2" opacity="0.9"/><rect x="1.6" y="2.6" width="12" height="4.8" rx="1" fill="white" opacity="0.9"/><rect x="19" y="3.5" width="2" height="3" rx="1" fill="white" opacity="0.55"/></svg>
        </div>
      </div>

      {/* Hero: weather icon + city */}
      <div className="sk-flat-hero">
        <svg className="sk-flat-icon" viewBox="0 0 140 96" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Sun */}
          <circle cx="52" cy="44" r="30" fill="#FFCC00"/>
          {/* Cloud bumps */}
          <circle cx="54" cy="62" r="19" fill="white"/>
          <circle cx="82" cy="56" r="24" fill="white"/>
          <circle cx="104" cy="64" r="16" fill="white"/>
          {/* Cloud base */}
          <rect x="35" y="68" width="85" height="24" rx="12" fill="white"/>
        </svg>

        <div className="sk-flat-temp">23°</div>
        <div className="sk-flat-city">Cupertino</div>
        <div className="sk-flat-cond">Partly Cloudy</div>
      </div>

      {/* Slide to unlock */}
      <div className="sk-flat-unlock">
        <span className="sk-flat-unlock-arrow">›</span>
        <span className="sk-flat-unlock-text">slide to unlock</span>
      </div>
    </div>
  )
}

function BlurPreview() {
  return (
    <div className="skill-mock skill-mock-blur">
      <img src="/OG.png" alt="" className="sk-blur-photo" />
      <div className="sk-blur-top">
        <div className="sk-blur-name">Rice Tseng</div>
        <div className="sk-blur-status">
          <span className="sk-blur-spinner" />
          Connecting
        </div>
      </div>

      {/* Progressive gradient blur — stacked layers each masked to a band */}
      <div className="sk-blur-gradient" aria-hidden="true">
        <div /><div /><div /><div /><div />
      </div>

      {/* Floating bottom — no container, elements rest on gradient blur */}
      <div className="sk-blur-bottom">
        <div className="sk-blur-user">
          <img src="/profile.jpg" className="sk-blur-avatar" alt="" />
          <div>
            <div className="sk-blur-uname">@ricetseng</div>
            <div className="sk-blur-time">23m ago</div>
          </div>
        </div>
        <div className="sk-blur-add">+ Add Member</div>
      </div>
    </div>
  )
}

function LiquidPreview() {
  return (
    <div className="skill-mock skill-mock-liquid">
      <img src="/apple_liquid_bg.jpg" alt="" className="sk-liquid-bg-img" />

      {/* SVG refraction filter applied to backdrop */}
      <svg className="sk-liquid-defs" aria-hidden="true">
        <defs>
          <filter id="sk-liquid-refract" x="-20%" y="-20%" width="140%" height="140%">
            <feTurbulence type="fractalNoise" baseFrequency="0.012" numOctaves="2" seed="4" result="turb" />
            <feGaussianBlur in="turb" stdDeviation="1.2" result="softTurb" />
            <feDisplacementMap in="SourceGraphic" in2="softTurb" scale="22" xChannelSelector="R" yChannelSelector="G" />
          </filter>
        </defs>
      </svg>

      <div className="sk-liquid-pill">
        <div className="sk-liquid-seg sk-liquid-seg--active">
          <svg className="sk-liquid-seg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 6a3 3 0 1 0 -3 3h12a3 3 0 1 0 -3 -3v12a3 3 0 1 0 3 -3H6a3 3 0 1 0 3 3z" />
          </svg>
          <span>Command</span>
        </div>
        <div className="sk-liquid-seg">
          <svg className="sk-liquid-seg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M22 12h-6l-2 3h-4l-2-3H2" />
            <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
          </svg>
          <span>Inbox</span>
        </div>
      </div>
    </div>
  )
}

const previews = {
  classic: <ClassicPreview />,
  aqua: <AquaPreview />,
  brushed: <BrushedPreview />,
  skeuomorphic: <SkeuomorphicPreview />,
  flat: <FlatPreview />,
  blur: <BlurPreview />,
  liquid: <LiquidPreview />,
}

const INSTALL_CMD = "npx skills add ricetseng/apple-material-era"

// ── Sound utilities ───────────────────────────────────────────
const _copyAudio = (() => {
  if (typeof window === 'undefined') return null
  const a = new Audio('/ReceivedMessage.wav')
  a.preload = 'auto'
  return a
})()

function playCopySound() {
  if (!_copyAudio) return
  try {
    const clone = _copyAudio.cloneNode()
    clone.volume = 0.65
    clone.play().catch(() => {})
  } catch(e) {}
}

// Synthesised low thud (replaces bomb-gp.glb which is a 3D model, not audio)
function playNavSound() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext
    if (!AudioCtx) return
    const ctx = new AudioCtx()
    const t   = ctx.currentTime

    // Sine sweep: 90 Hz → 28 Hz (the "bomb" thud)
    const osc = ctx.createOscillator()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(90, t)
    osc.frequency.exponentialRampToValueAtTime(28, t + 0.22)
    const oscG = ctx.createGain()
    oscG.gain.setValueAtTime(0.55, t)
    oscG.gain.exponentialRampToValueAtTime(0.001, t + 0.24)
    osc.connect(oscG); oscG.connect(ctx.destination)
    osc.start(t); osc.stop(t + 0.24)

    // Short noise click at attack
    const bufLen = Math.floor(ctx.sampleRate * 0.035)
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate)
    const d = buf.getChannelData(0)
    for (let i = 0; i < bufLen; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / bufLen)
    const click = ctx.createBufferSource()
    click.buffer = buf
    const lpf = ctx.createBiquadFilter()
    lpf.type = 'lowpass'; lpf.frequency.value = 220
    const clickG = ctx.createGain()
    clickG.gain.setValueAtTime(0.38, t)
    click.connect(lpf); lpf.connect(clickG); clickG.connect(ctx.destination)
    click.start(t)

    setTimeout(() => ctx.close(), 500)
  } catch(e) {}
}

// ── Soft-blur-in entrance animation (spec: soft-blur-in, site-scaled runtime) ─
const SBI_DURATION = 0.648        // 900ms × 0.72
const SBI_STAGGER  = 0.018        // 25ms  × 0.72
const SBI_EASE     = [0.22, 1, 0.36, 1]
const SBI_Y        = 9.3          // 16px  × 0.58

function SoftBlurChars({ text, startIdx = 0, baseDelay = 0 }) {
  return (
    <>
      {Array.from(text).map((char, i) => (
        <motion.span
          key={i}
          style={{
            display:            'inline-block',
            whiteSpace:         'pre',
            backfaceVisibility: 'hidden',
            willChange:         'transform, opacity, filter',
          }}
          initial={{ opacity: 0, y: SBI_Y, filter: 'blur(12px)' }}
          animate={{ opacity: 1, y: 0,     filter: 'blur(0px)'  }}
          transition={{
            delay:    baseDelay + (startIdx + i) * SBI_STAGGER,
            duration: SBI_DURATION,
            ease:     SBI_EASE,
          }}
        >
          {char}
        </motion.span>
      ))}
    </>
  )
}

function IconCopy() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  )
}

function IconCheck() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function CopyCommand({ command }) {
  const [copied, setCopied] = useState(false)
  function copy(e) {
    e.stopPropagation()
    playCopySound()
    navigator.clipboard.writeText(command).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }
  return (
    <div className="skill-era-command-row">
      <code className="skill-era-command">{command}</code>
      <button className={`skill-era-copy-btn${copied ? " copied" : ""}`} onClick={copy} title="Copy">
        {copied ? <IconCheck /> : <IconCopy />}
      </button>
    </div>
  )
}


function InstallPill({ className = "" }) {
  const [copied, setCopied] = useState(false)
  function copy() {
    playCopySound()
    navigator.clipboard.writeText(INSTALL_CMD).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }
  return (
    <button className={`skill-install-pill${className ? ` ${className}` : ""}`} onClick={copy}>
      <span className="skill-install-prefix">$</span>
      <span className="skill-install-cmd">{INSTALL_CMD}</span>
      <span className={`skill-install-icon${copied ? " copied" : ""}`}>
        {copied ? <IconCheck /> : <IconCopy />}
      </span>
    </button>
  )
}

/* ── Per-card scroll-driven component ──────────────────────────── */
/* ── Per-card 3D component ──────────────────────────────────────
   offset = i - activeIdx
   · 0        → active: pops toward viewer
   · ±1, ±2…  → inactive: pushed back, tilted, dimmed
   Parallax: distant cards use softer springs → move slower than active.
──────────────────────────────────────────────────────────────── */
function EraCard({ era, offset, onClick }) {
  const abs  = Math.abs(offset)
  if (abs > 3) return null

  const sign     = Math.sign(offset) || 0
  const isActive = abs === 0

  // ── Depth values ──────────────────────────────────────────────
  const z    = isActive ? 80   : -180 - (abs - 1) * 80
  const y    = isActive ? 0    : sign * (130 + (abs - 1) * 68)
  const rotX = isActive ? 0    : sign * (18  + (abs - 1) * 4)
  const scl  = isActive ? 1    : Math.max(0.55, 0.82 - (abs - 1) * 0.09)
  const opa  = isActive ? 1    : Math.max(0.06, 0.45 - (abs - 1) * 0.14)
  // Blur lives on the INNER div, not the card wrapper.
  // This keeps blur clipped by overflow:hidden + border-radius → crisp corners.
  const blr  = isActive ? 0 : Math.min(4, abs * 1.5)

  // Parallax feel: distant cards use softer springs → lag behind active
  const spring = {
    type:      'spring',
    stiffness: Math.max(120, 310 - abs * 55),
    damping:   Math.min(50,  34  + abs * 4),
    mass:      0.75 + abs * 0.15,
  }

  return (
    <motion.div
      className="sk2-era-card"
      style={{ zIndex: 100 - abs * 10, cursor: isActive ? 'default' : 'pointer' }}
      animate={{ y, z, rotateX: rotX, scale: scl, opacity: opa }}
      transition={spring}
      onClick={!isActive ? onClick : undefined}
      whileHover={!isActive ? {
        scale:   scl * 1.04,
        opacity: Math.min(1, opa + 0.18),
        transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] },
      } : undefined}
    >
      {/* Blur + inner parallax on the content div — clipped by card's border-radius */}
      <div
        className={`sk2-card-inner skill-era-${era.id}`}
        style={{
          filter:    blr > 0 ? `blur(${blr}px)` : undefined,
          transform: abs > 0 ? `translateY(${-sign * 10}px)` : undefined,
        }}
      >
        <div className="skill-era-preview sk2-era-preview">{previews[era.id]}</div>
      </div>
    </motion.div>
  )
}

export default function Skill() {
  const [activeIdx, setActiveIdx] = useState(4)
  const [dark, setDark]           = useState(false)
  const [isMobile, setIsMobile]   = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768)
  const [direction, setDirection] = useState(1)
  const pageRef                   = useRef(null)
  const sbiDelay                  = useRef(Math.random() * 0.4).current
  const touchStartX               = useRef(0)
  const touchStartY               = useRef(0)

  // Tab title
  useEffect(() => {
    const prev = document.title
    document.title = 'Apple SKILLS.MD'
    return () => { document.title = prev }
  }, [])

  // Detect mobile / desktop on resize
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Startup chime — plays once on load; falls back to first interaction if autoplay blocked
  useEffect(() => {
    const audio = new Audio('/StartupIntelPreT2Mac.wav')
    audio.volume = 0.75
    const p = audio.play()
    if (p) {
      p.catch(() => {
        const unlock = () => {
          audio.play().catch(() => {})
          window.removeEventListener('pointerdown', unlock)
          window.removeEventListener('keydown', unlock)
        }
        window.addEventListener('pointerdown', unlock, { once: true })
        window.addEventListener('keydown', unlock, { once: true })
      })
    }
    return () => { try { audio.pause(); audio.src = '' } catch(e) {} }
  }, [])

  // Navigate with nav sound + direction tracking
  function navigate(newIdxOrUpdater) {
    if (typeof newIdxOrUpdater === 'function') {
      setActiveIdx(prev => {
        const next = newIdxOrUpdater(prev)
        if (next !== prev) {
          setDirection(next > prev ? 1 : -1)
          playNavSound()
        }
        return next
      })
    } else {
      setActiveIdx(prev => {
        if (newIdxOrUpdater !== prev) {
          setDirection(newIdxOrUpdater > prev ? 1 : -1)
          playNavSound()
        }
        return newIdxOrUpdater
      })
    }
  }

  // Touch handlers for mobile swipe navigation
  function onTouchStart(e) {
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
  }

  function onTouchEnd(e) {
    const dx = e.changedTouches[0].clientX - touchStartX.current
    const dy = e.changedTouches[0].clientY - touchStartY.current
    if (Math.abs(dx) < 40 && Math.abs(dy) < 40) return
    if (Math.abs(dy) >= Math.abs(dx)) {
      if (dy < -40) navigate(i => Math.min(eras.length - 1, i + 1))
      else if (dy > 40) navigate(i => Math.max(0, i - 1))
    } else {
      if (dx < -40) navigate(i => Math.min(eras.length - 1, i + 1))
      else if (dx > 40) navigate(i => Math.max(0, i - 1))
    }
  }

  // Mouse-wheel / trackpad swipe → navigate the deck (desktop only)
  useEffect(() => {
    const el = pageRef.current
    if (!el) return
    let lastT = 0
    function onWheel(e) {
      e.preventDefault()
      const now = Date.now()
      if (now - lastT < 550) return
      const dir = e.deltaY > 0 ? 1 : -1
      navigate(i => Math.max(0, Math.min(eras.length - 1, i + dir)))
      lastT = now
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [isMobile])  // eslint-disable-line

  // ── Mobile layout ─────────────────────────────────────────────────────────
  if (isMobile) {
    const era = eras[activeIdx]
    return (
      <div className={`sk2-mobile${dark ? ' sk2m--dark' : ''}`}>

        {/* Brand + headline — mirrors desktop left panel */}
        <div className="sk2m-hero">
          <div className="sk2m-brand">SKILLS.MD</div>
          <h1 className="sk2m-title">A time machine<br/>for Apple<br/>UI materials.</h1>
        </div>

        {/* Dominant card — full touch target */}
        <div
          className="sk2m-stage"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              key={era.id}
              className="sk2m-card-wrap"
              initial={{ opacity: 0, scale: 0.90, y: direction * 56 }}
              animate={{ opacity: 1, scale: 1,    y: 0 }}
              exit={{    opacity: 0, scale: 0.90, y: -direction * 56 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="sk2m-card">
                <div className={`sk2m-card-inner skill-era-${era.id}`}>
                  <div className="skill-era-preview sk2m-era-preview">
                    {previews[era.id]}
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {activeIdx > 0 && (
            <button className="sk2m-arrow sk2m-arrow--up" onClick={() => navigate(activeIdx - 1)} aria-label="Previous era">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
            </button>
          )}
          {activeIdx < eras.length - 1 && (
            <button className="sk2m-arrow sk2m-arrow--down" onClick={() => navigate(activeIdx + 1)} aria-label="Next era">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
          )}
        </div>

        {/* Era info */}
        <AnimatePresence mode="wait">
          <motion.div
            key={era.id + '-meta'}
            className="sk2m-meta"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{    opacity: 0, y: -8 }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="sk2m-era-year">{era.year}</span>
            <h2 className="sk2m-era-name">{era.name}</h2>
            <p className="sk2m-era-phil">{era.philosophy}</p>
          </motion.div>
        </AnimatePresence>

        {/* Timeline dots */}
        <nav className="sk2m-timeline">
          {eras.map((e, i) => (
            <button
              key={e.id}
              className={`sk2m-dot${i === activeIdx ? ' sk2m-dot--on' : ''}`}
              onClick={() => navigate(i)}
              aria-label={e.name}
            >
              <span className="sk2m-dot-num">{String(i + 1).padStart(2, '0')}</span>
              <span className="sk2m-dot-bar" />
            </button>
          ))}
        </nav>

        {/* Footer: Rice Tseng logo + theme toggle — very bottom */}
        <div className="sk2m-footer">
          <a href="/" className="sk2m-footer-logo">Rice Tseng →</a>
          <button className="sk2m-mode-btn" onClick={() => setDark(d => !d)} aria-label="Toggle theme">
            {dark ? '☀' : '☾'}
          </button>
        </div>

      </div>
    )
  }

  // ── Desktop layout ─────────────────────────────────────────────────────────
  return (
    <div className={`sk2-page${dark ? ' sk2--dark' : ''}`} ref={pageRef}>

      {/* ── LEFT: product & install info ── */}
      <aside className="sk2-left">
        <motion.div
          className="sk2-brand"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: sbiDelay, duration: 0.5, ease: SBI_EASE }}
        >
          SKILLS.MD
        </motion.div>
        <div className="sk2-left-body">
          <h1 className="sk2-headline" aria-label="A time machine for Apple UI materials.">
            {(() => {
              const lines = ['A time', 'machine', 'for Apple', 'UI materials.']
              let idx = 0
              return lines.map((line, li) => {
                const start = idx
                idx += line.length
                return (
                  <span key={li}>
                    <SoftBlurChars text={line} startIdx={start} baseDelay={sbiDelay} />
                    {li < lines.length - 1 && <br />}
                  </span>
                )
              })
            })()}
          </h1>
          <motion.p
            className="sk2-blurb"
            initial={{ opacity: 0, y: 6, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ delay: sbiDelay + 0.45, duration: 0.7, ease: SBI_EASE }}
          >
            A material time machine for designers and AI agents. Explore Apple's iconic UI eras and apply their depth, texture, light, and motion to modern interfaces.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: sbiDelay + 0.6, duration: 0.6, ease: SBI_EASE }}
          >
            <InstallPill />
          </motion.div>
        </div>
        <div className="sk2-left-foot">
          <div className="sk2-foot-row">
            <button className="sk2-mode-btn" onClick={() => setDark(d => !d)}>
              {dark ? '☀ Light' : '☾ Dark'}
            </button>
          </div>
        </div>
      </aside>

      {/* ── CENTER: fixed 3D stage — perspective set in CSS on .sk2-stage ── */}
      <main className="sk2-center">
        <div className="sk2-stage">
          {eras.map((e, i) => (
            <EraCard
              key={e.id}
              era={e}
              offset={i - activeIdx}
              onClick={() => navigate(i)}
            />
          ))}
        </div>

        <div className="sk2-counter">
          <span className="sk2-ctr-n">{String(activeIdx + 1).padStart(2, '0')}</span>
          <span className="sk2-ctr-sep"> / </span>
          <span className="sk2-ctr-total">{String(eras.length).padStart(2, '0')}</span>
        </div>
      </main>

      {/* ── RIGHT: era timeline ── */}
      <nav className="sk2-right">
        <div className="sk2-tl-range">1984 → 2026</div>
        <div className="sk2-list">
          {eras.map((e, i) => {
            const startYear = e.year.split(/[–—→]/)[0].trim()
            return (
              <button
                key={e.id}
                className={`sk2-row${i === activeIdx ? ' sk2-row--active' : ''}`}
                onClick={() => navigate(i)}
              >
                <div className="sk2-row-top">
                  <span className="sk2-row-year-mark">{startYear}</span>
                  <span className="sk2-row-name">{e.name}</span>
                </div>
                <div className="sk2-row-detail">
                  <p className="sk2-row-phil">{e.philosophy}</p>
                  <CopyCommand command={e.command} />
                </div>
              </button>
            )
          })}
        </div>
      </nav>

      <a href="/" className="sk2-made-by">
        <span className="sk2-made-by-label">Made by </span>Rice Tseng →
      </a>

    </div>
  )
}

